import { ColumnInstance, useTable } from 'react-table'
import { useDispatch } from 'react-redux'
import { useMemo, useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { KTCardBody } from 'src/_metronic/helpers'
import { DeletedTransactionsColumns } from './columns/_columns'
import { DeletedTransactions } from '../core/_models'
import { useQueryResponseData, useQueryResponseLoading } from '../core/QueryResponseProvider'
import { CustomHeaderColumn } from './columns/DeletedTransactionsHeaderColumn'
import { DeletedTransactionsListPagination } from '../components/pagination/DeletedTransactionsListPagination'
import { DeletedTransactionsListLoading } from '../components/loading/DeletedTransactionsListLoading'

const DeletedTransactionsTable = () => {
  const Parties = useQueryResponseData()
  const isLoading = useQueryResponseLoading()
  const data = useMemo(() => Parties, [Parties])
  const navigate = useNavigate()
  const columns = useMemo(() => DeletedTransactionsColumns, [])
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data,
  })
  const dispatch = useDispatch()
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null)
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);
  useEffect(() => {
    if (selectedRowIndex !== null && rowRefs.current[selectedRowIndex]) {
      rowRefs.current[selectedRowIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [selectedRowIndex]);
  const handleRowClick = async (row: any, index: number) => {
    setSelectedRowIndex(index)
    if (tableContainerRef.current) {
      tableContainerRef.current.focus()
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!rows.length) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setSelectedRowIndex((prevIndex) => {
        if (prevIndex === null) return 0
        return Math.min(prevIndex + 1, rows.length - 1)
      })
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setSelectedRowIndex((prevIndex) => {
        if (prevIndex === null) return 0
        return Math.max(prevIndex - 1, 0)
      })
    }
  }

  return (
    <KTCardBody className='py-4'>
      <div 
        className='table-responsive'
        ref={tableContainerRef}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        style={{ outline: 'none' }}
      >
        <table
          id='kt_table_DeletedTransactions'
          className='table align-middle table-row-dashed fs-6 gy-5 table-hover dataTable no-footer'
          {...getTableProps()}
        >
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr
                {...headerGroup.getHeaderGroupProps()}
                className='text-center text-gray-700 fw-bolder fs-6 text-uppercase gs-0'
              >
                {headerGroup.headers.map((column: ColumnInstance<DeletedTransactions>) => (
                  <CustomHeaderColumn key={column.id} column={column} />
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.length > 0 ? (
              rows.map((row: any, i) => {
                prepareRow(row)
                const isSelected = i === selectedRowIndex
                return (
                  <tr
                    {...row.getRowProps()}
                    key={`row-${i}-${row.id}`}
                    className='text-gray-600 cur'
                    ref={el => rowRefs.current[i] = el}

                    onClick={() => handleRowClick(row, i)}
                    style={{
                      cursor: 'pointer',
                      outline: isSelected ? '2px solid blue' : 'none',
                      outlineOffset: '-2px',
                    }}
                  >
                    {row.cells.map((cell: any, index: any) => {
                      const cellProps = cell.getCellProps()
                      const isSenderColumn = cell.column.id === 'source';
                      const isReceiverColumn = cell.column.id === 'destination';
                      const stopRowClick = isSenderColumn || isReceiverColumn;

                      return (
                        <td
                          {...cellProps}
                          key={index}
                          className={'clickable-column'}
                          style={{
                            fontWeight: 'bold',
                            color: 'black',
                            textAlign: 'center',
                            userSelect: 'none',
                            border: 'none',
                          }}
                          onClick={stopRowClick ? (e) => e.stopPropagation() : undefined}
                        >
                          {cell.render('Cell')}
                        </td>
                      )
                    })}
                  </tr>
                )
              })
            ) : (
              <tr>
                <td colSpan={columns.length}>
                  <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                    No matching records found
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <DeletedTransactionsListPagination />
      {isLoading && <DeletedTransactionsListLoading />}
    </KTCardBody>
  )
}

export { DeletedTransactionsTable }
